<template>
    <div class="w-full relative">
        <template v-if="is_loading">
            <div class="h-96 py-32 text-center">
                <LoadingOutlined :style="{ fontSize: '24px' }" />
            </div>
        </template>
        <template v-if="!is_loading">
            <div class="absolute top-5 left-0">
                {{ $t('dashboard.graph_title.total', { total: total }) }}
            </div>
            <div class="absolute top-0 right-0">
                <a-dropdown
                    :trigger="['click']"
                    class="text-default-blue-green hover:text-highlight-blue-green"
                    v-if="isSubFiltersAvailable"
                >
                    <span class="ant-dropdown-link cursor-pointer">
                        {{ currentSubFilter }}
                        <DownOutlined />
                    </span>
                    <template #overlay>
                        <a-menu @click="handleSubFilters">
                            <template v-for="item in sub_filter_options" :key="item.value">
                                <a-menu-item>
                                    {{ item.text }}
                                </a-menu-item>
                            </template>
                        </a-menu>
                    </template>
                </a-dropdown>
                <a
                    v-if="view_more"
                    data-cy="view-more"
                    @click="handleViewMore"
                    class="underline text-default-blue-green hover:text-highlight-blue-green hover:no-underline hover:cursor-pointer pl-2"
                >
                    {{ view_more_title }}
                </a>
            </div>
            <Bar
                v-if="!is_init"
                :data-cy="'bar-chart-' + chart_id"
                :options="chart_options"
                :data="chart_data"
                :chart-id="chart_id"
                :dataset-id-key="dataset_id_key"
                :plugins="plugins"
                :css-classes="css_classes"
                :styles="styles"
                :height="graph_height"
            />
        </template>
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, BarElement, CategoryScale, LinearScale } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons-vue';

ChartJS.register(Tooltip, BarElement, CategoryScale, LinearScale, ChartDataLabels);

export default {
    components: { Bar, DownOutlined, LoadingOutlined },
    props: {
        chart_id: {
            type: String,
            default: 'bar-chart',
        },
        title: {
            type: String,
            default: '',
        },
        dataset_endpoint: {
            type: String,
            default: '',
        },
        filter_params: {
            type: Object,
            default: () => {},
        },
        filter_limit: {
            type: Number,
            default: 0,
        },
        default_sub_filter: {
            type: String,
            default: '',
        },
        sub_filter_options: {
            type: Array,
            default: () => [],
        },
        dataset_id_key: {
            type: String,
            default: 'label',
        },
        css_classes: {
            type: String,
            default: 'w-full',
        },
        styles: {
            type: Object,
            default: () => {},
        },
        graph_height: {
            type: Number,
            default: 400,
        },
        plugins: {
            type: Array,
            default: () => [
                Title,
                {
                    id: 'emptyBarHorizontal',
                    afterDraw: function (chart) {
                        const { datasets } = chart.data;
                        let has_data = false;

                        for (let i = 0; i < datasets.length; i += 1) {
                            const dataset = datasets[i];
                            has_data |= dataset.data.length > 0;
                        }

                        if (!has_data) {
                            // No data is present
                            const { ctx, width, height } = chart;

                            ctx.save();
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';
                            ctx.font = "14px normal 'Open Sans'";
                            ctx.fillText('No Data found for the selected period', width / 2, height / 2);
                            ctx.restore();
                        }
                    },
                },
            ],
        },
        view_more: {
            type: Boolean,
            default: false,
        },
        view_more_title: {
            type: String,
            default: '',
        },
        view_more_limit: {
            type: Number,
            default: 0,
        },
    },
    watch: {
        filter_params: {
            immediate: true,
            deep: true,
            handler() {
                if (!this.is_init) {
                    this.loadData();
                }
            },
        },
    },
    computed: {
        isSubFiltersAvailable() {
            return this.sub_filter_options.length > 0;
        },
        currentSubFilter() {
            let visible_text = '';
            let that = this;

            _.forEach(this.sub_filter_options, function (item) {
                if (item.value === that.sub_filter) {
                    visible_text = item.text;
                }
            });

            return visible_text;
        },
    },
    data() {
        return {
            is_loading: false,
            is_init: true,
            sub_filter: '',
            meta_data: [],
            total: 0,
            labels: [],
            dataset_options: {
                backgroundColor: '#2563EB',
                barThickness: 40,
                borderColor: '#2563EB',
                borderRadius: 5,
                maxBarThickness: 25,
                minBarLength: 20,
                data: [],
            },
            chart_data: {},
            chart_options: {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                plugins: {
                    title: {
                        align: 'start',
                        display: true,
                        padding: {
                            top: 6,
                            bottom: 35,
                        },
                        text: this.title,
                    },
                    datalabels: {
                        anchor: 'start',
                        align: 'end',
                        color: 'white',
                        formatter: function (value) {
                            return value.x;
                        },
                    },
                    legend: {
                        display: false,
                    },
                },
                scales: {
                    x: {
                        grid: {
                            display: false,
                        },
                        ticks: {
                            precision: 0,
                        },
                    },
                    y: {
                        grid: {
                            display: false,
                        },
                    },
                },
            },
        };
    },
    mounted() {
        let that = this;

        if (this.chart_id === 'upload_frequency') {
            this.chart_options.plugins.datalabels.formatter = function (value, context) {
                let amount = that.meta_data.record_counts[context.dataIndex];

                return parseFloat(value).toLocaleString() + ' (' + parseFloat(amount).toLocaleString() + ')';
            };
        } else {
            this.chart_options.plugins.datalabels.formatter = function (value, context) {
                return parseFloat(value).toLocaleString();
            };
        }

        this.sub_filter = this.default_sub_filter;
        this.loadData();
    },
    methods: {
        loadData() {
            if (this.is_loading) {
                return false;
            }

            this.total = 0;
            this.chart_data = {
                labels: [],
                datasets: [],
            };
            this.is_loading = true;

            if (this.dataset_endpoint.length == 0) {
                this.$notification.error({
                    message: this.$t('dashboard.notifications.error.missing_dataset_endpoint', {
                        graph: this.chart_id,
                    }),
                });

                this.is_loading = false;

                return false;
            }

            let date_filters = {
                current_filter: this.filter_params.current_filter,
            };

            if (this.filter_params.current_filter == 'custom') {
                let dateFormat = 'YYYY-MM-DD';
                let start_date = this.$dayjs(this.filter_params.date_ranges.start_date, dateFormat, true);
                let end_date = this.$dayjs(this.filter_params.date_ranges.end_date, dateFormat, true);

                if (!start_date.isValid() || !end_date.isValid() || start_date.isAfter(end_date)) {
                    this.is_loading = false;
                    return false;
                }

                date_filters.date_ranges = {
                    start_date: this.$dayjs(this.filter_params.date_ranges.start_date).format('YYYY-MM-DD'),
                    end_date: this.$dayjs(this.filter_params.date_ranges.end_date).format('YYYY-MM-DD'),
                };
            }

            let params = {
                ...date_filters,
                sub_filter: this.sub_filter,
                limit: this.filter_limit,
            };

            if (this.$store.getters['user/isMultiAssociation']) {
                params.association_id = this.$store.getters['user/currentAssociationId'];
            } else {
                params.association_id = this.$store.getters['user/associationId'];
            }

            this.$http
                .post(this.dataset_endpoint, params)
                .then((response) => {
                    let dataset = { ...this.dataset_options };

                    dataset.data = response.data.data;

                    this.meta_data = response.data.meta;
                    this.total = parseFloat(response.data.meta.total).toLocaleString();
                    this.labels = response.data.meta.labels;
                    this.chart_data = {
                        labels: this.labels,
                        datasets: [dataset],
                    };
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVGBH01' }),
                        });
                    }
                })
                .finally(() => {
                    this.is_loading = false;
                    this.is_init = false;
                });
        },
        handleSubFilters({ key }) {
            this.sub_filter = key;
            this.loadData();
        },
        handleViewMore() {
            this.$emit('viewMoreInfo', {
                type: this.chart_id,
                dataset_endpoint: this.dataset_endpoint,
                limit: this.view_more_limit,
                sub_filter_options: this.sub_filter_options,
                sub_filter: this.sub_filter,
            });
        },
    },
};
</script>
